/**
 * VoorraadGroepDetail - Detailweergave van een voorraadgroep met subgroepen en items
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiFetch from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorDialog from '../../components/ErrorDialog';
import './Voorraad.css';

interface VoorraadCategorie {
  id: number;
  code: string;
  naam: string;
}

interface VoorraadGroep {
  id: number;
  parent_id: number | null;
  categorie_id: number;
  naam: string;
  niveau: number;
  volgorde: number;
  actief: boolean;
  children_count: number;
  items_count: number;
}

interface VoorraadItem {
  id: number;
  groep_id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  leverancier: string | null;
  artikelnummer: string | null;
  omschrijving: string | null;
  levensduur_maanden: number | null;
  specificatie_1: string | null;
  specificatie_2: string | null;
  specificatie_3: string | null;
  aantal_voorraad: number;
  min_voorraad: number | null;
  inkoopprijs: number | null;
  verkoopprijs: number | null;
  prijs_per_verpakking: number | null;
  verpakkingseenheid: number | null;
  actief: boolean;
  status: string;
}

const VoorraadGroepDetail = () => {
  const { groepId } = useParams<{ groepId: string }>();
  const [groep, setGroep] = useState<VoorraadGroep | null>(null);
  const [categorie, setCategorie] = useState<VoorraadCategorie | null>(null);
  const [subGroepen, setSubGroepen] = useState<VoorraadGroep[]>([]);
  const [items, setItems] = useState<VoorraadItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<VoorraadItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const tableHeaderRef = React.useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    loadData();
  }, [groepId]);

  useEffect(() => {
    // Filter items op basis van zoekterm
    if (searchTerm.trim() === '') {
      setFilteredItems(items);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = items.filter(item =>
        item.onderdeel_type.toLowerCase().includes(term) ||
        (item.fabrikant && item.fabrikant.toLowerCase().includes(term)) ||
        (item.model && item.model.toLowerCase().includes(term)) ||
        (item.leverancier && item.leverancier.toLowerCase().includes(term)) ||
        (item.artikelnummer && item.artikelnummer.toLowerCase().includes(term))
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, items]);

  // Sticky header scroll listener
  useEffect(() => {
    if (!filteredItems.length) return; // Wait for items to load

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185

        // Always update position when scrolling
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll(); // Check initial position
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [filteredItems]);

  const loadData = async () => {
    if (!groepId) return;

    try {
      setLoading(true);
      setError(null);

      // Laad groep details
      const groepData = await apiFetch(`/api/v1/voorraad/groepen/${groepId}`);
      setGroep(groepData);

      // Laad categorie
      const categorieData = await apiFetch(`/api/v1/voorraad/categorieen/${groepData.categorie_id}`);
      setCategorie(categorieData);

      // Laad directe subgroepen (voor dropdown)
      const subGroepenData = await apiFetch(`/api/v1/voorraad/groepen?categorie_id=${groepData.categorie_id}&parent_id=${groepId}`);
      setSubGroepen(subGroepenData);

      // Laad alle subgroepen recursief (voor items)
      const alleSubGroepen = await apiFetch(`/api/v1/voorraad/groepen?categorie_id=${groepData.categorie_id}&alle=true`);

      // Vind alle groep IDs die onder deze groep vallen (inclusief deze groep zelf)
      const getChildIds = (parentId: number): number[] => {
        const children = alleSubGroepen.filter((g: VoorraadGroep) => g.parent_id === parentId);
        let ids = [parentId];
        for (const child of children) {
          ids = [...ids, ...getChildIds(child.id)];
        }
        return ids;
      };
      const groepIds = getChildIds(parseInt(groepId));

      // Laad items van deze groep en alle subgroepen
      const allItems: VoorraadItem[] = [];
      for (const gid of groepIds) {
        const itemsData = await apiFetch(`/api/v1/voorraad/items?groep_id=${gid}`);
        allItems.push(...itemsData);
      }
      setItems(allItems);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van groep');
      console.error('Error loading groep:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    try {
      setDeleting(true);
      await apiFetch(`/api/v1/voorraad/items/${itemId}`, {
        method: 'DELETE'
      });
      // Verwijder item uit lijst
      setItems(items.filter(item => item.id !== itemId));
      setDeleteItemId(null);
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen van item');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Producten</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Groep laden...
        </div>
      </div>
    );
  }

  if (!groep || !categorie) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Producten</h1>
        </div>
        <div className="error-message">
          Groep niet gevonden
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{groep.naam}</h1>
        <button
          className="btn-primary"
          onClick={() => navigate(`/onderdelen/new?groep_id=${groep.id}`)}
        >
          + Nieuw Item
        </button>
      </div>

      {/* Zoekbalk en Filter dropdown op één regel */}
      <div className="search-bar">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <input
            type="text"
            placeholder="Zoek op type, fabrikant, model, leverancier of artikelnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-results">
            {filteredItems.length} {filteredItems.length === 1 ? 'resultaat' : 'resultaten'}
          </span>
        </div>

        {/* Subgroepen dropdown */}
        {subGroepen.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) {
                navigate(`/voorraad/groep/${e.target.value}`);
              }
            }}
            style={{
              padding: '0.625rem 1rem',
              fontSize: '0.9375rem',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#1e293b',
              cursor: 'pointer',
              minWidth: '200px',
              outline: 'none'
            }}
          >
            <option value="">Selecteer subgroep...</option>
            {subGroepen.map(subGroep => (
              <option key={subGroep.id} value={subGroep.id.toString()}>
                {subGroep.naam} ({subGroep.items_count} items)
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Vaste header rij (altijd zichtbaar) */}
      {filteredItems.length > 0 && (
        <div ref={tableHeaderRef} style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Fabrikant</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Model</div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Leverancier</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Artikelnr.</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Voorraad</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '2.2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Sticky header overlay (duplicaat) */}
      {isHeaderSticky && (
        <div style={{
          position: 'fixed',
          top: '185px',
          left: `${tableLeft}px`,
          width: `${tableWidth}px`,
          zIndex: 1000,
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          borderRadius: '8px 8px 0 0',
          padding: '0.75rem 1rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Fabrikant</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Model</div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Leverancier</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Artikelnr.</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Voorraad</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '2.2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Items lijst */}
      {filteredItems.length > 0 && (
        <div>
          <div style={{
            background: 'white',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {filteredItems.map(item => (
              <div key={item.id} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1rem',
                borderBottom: '1px solid #dee2e6',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <div style={{ flex: '1.2', minWidth: 0, fontWeight: 500, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.onderdeel_type}</div>
                <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.fabrikant || '-'}</div>
                <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.model || '-'}</div>
                <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.leverancier || '-'}</div>
                <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.artikelnummer || '-'}</div>
                <div style={{ flex: '0.6', minWidth: 0, fontWeight: 500, paddingRight: '1rem' }}>{item.aantal_voorraad}</div>
                <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem', display: 'flex', alignItems: 'center' }}>
                  <span className={`status-badge status-${item.status}`}>
                    {item.status === 'kritiek' ? 'Kritiek' : item.status === 'waarschuwing' ? 'Laag' : 'OK'}
                  </span>
                </div>
                <div className="actions" style={{ flex: '2.2', minWidth: 0 }}>
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => navigate(`/onderdelen/${item.id}`)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Bewerken
                  </button>
                  <button
                    className="btn-small btn-success"
                    onClick={() => navigate(`/onderdelen/${item.id}/documenten`)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Documenten
                  </button>
                  <button
                    className="btn-small btn-danger"
                    onClick={() => setDeleteItemId(item.id)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Verwijder
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f0f9ff',
          borderRadius: '8px',
          fontSize: '0.9rem'
        }}>
          <strong>Totaal:</strong> {items.filter(i => i.aantal_voorraad > 0).length} Producten (van {items.length} producten) op voorraad totaal aantal {items.reduce((sum, i) => sum + i.aantal_voorraad, 0)}
        </div>
      )}

      {items.length === 0 && subGroepen.length === 0 ? (
        <div style={{
          padding: '2rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p>Nog geen items in deze groep.</p>
          <button
            className="btn-primary"
            onClick={() => navigate(`/onderdelen/new?groep_id=${groep.id}`)}
            style={{ marginTop: '1rem' }}
          >
            Voeg eerste item toe
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={!!deleteItemId}
        title="Item Verwijderen"
        message={(() => {
          const item = items.find(i => i.id === deleteItemId);
          if (!item) return 'Weet je zeker dat je dit item wilt verwijderen?';
          const naam = [item.fabrikant, item.model].filter(Boolean).join(' ') || item.onderdeel_type;
          return `Weet je zeker dat je de "${naam}" wilt verwijderen?`;
        })()}
        confirmText={deleting ? 'Verwijderen...' : 'Verwijderen'}
        cancelText="Annuleren"
        onConfirm={() => deleteItemId && handleDeleteItem(deleteItemId)}
        onCancel={() => setDeleteItemId(null)}
        danger={true}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default VoorraadGroepDetail;
